import {
  format, parseISO, isSameDay, isSameMonth,
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays,
} from "date-fns";
import { User } from "lucide-react";
import { WT_COLOR } from "@/lib/schedule";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function MonthGrid({ monthDate, jobs, onDayClick, onEventClick }) {
  const gridStart = startOfWeek(startOfMonth(monthDate), { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(monthDate), { weekStartsOn: 1 });
  const days = [];
  let d = gridStart;
  while (d <= gridEnd) {
    days.push(d);
    d = addDays(d, 1);
  }

  const jobsFor = (day) =>
    jobs
      .filter((j) => j.scheduled_date && isSameDay(parseISO(j.scheduled_date), day))
      .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

  return (
    <div className="border border-border/60 rounded-sm overflow-hidden" data-testid="month-grid">
      <div className="grid grid-cols-7 border-b border-border/60">
        {WEEKDAYS.map((w) => (
          <div key={w} className="label-caps text-muted-foreground text-center py-3 border-l border-border/40 first:border-l-0">
            {w}
          </div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((day) => {
          const dayJobs = jobsFor(day);
          const inMonth = isSameMonth(day, monthDate);
          const isToday = isSameDay(day, new Date());
          return (
            <button
              key={day.toISOString()}
              onClick={() => onDayClick && onDayClick(day)}
              data-testid={`month-day-${format(day, "yyyy-MM-dd")}`}
              className={`text-left min-h-[120px] p-2 border-l border-t border-border/40 first:border-l-0 align-top transition-colors hover:bg-accent/40 ${inMonth ? "bg-background" : "bg-muted/30"}`}
            >
              <div className={`font-serif text-lg mb-1 ${isToday ? "text-brand" : inMonth ? "text-foreground" : "text-muted-foreground"}`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {dayJobs.slice(0, 3).map((j) => {
                  const wt = j.kind === "walkthrough";
                  const color = j.team?.[0]?.color || "#f21b7a";
                  const techNames = (j.team || []).map((t) => t.name).filter(Boolean).join(", ");
                  return (
                    <div
                      key={j.id}
                      onClick={(e) => { e.stopPropagation(); onEventClick && onEventClick(j); }}
                      data-testid={`month-job-${j.id}`}
                      style={j.status === "paused" || j.status === "cancelled"
                        ? { borderLeftColor: "#b8b0bb", backgroundColor: "hsl(300 8% 94%)", opacity: 0.65 }
                        : wt ? { borderLeftColor: WT_COLOR, backgroundColor: `${WT_COLOR}1f`, borderStyle: "dashed" }
                        : { borderLeftColor: color, backgroundColor: `${color}26` }}
                      className="border-l-4 rounded-sm px-1.5 py-0.5 text-[11px] leading-tight cursor-pointer"
                    >
                      <div className="truncate">{j.status === "paused" && "Paused · "}{j.status === "cancelled" && <span className="line-through">Cancelled · </span>}{wt && <span className="font-semibold" style={{ color: WT_COLOR }}>Walk-through · </span>}{format(parseISO(j.scheduled_date), "h:mma")} {j.customer?.name || "—"}</div>
                      {!wt && j.price != null && <div className="truncate font-semibold" data-testid={`month-job-price-${j.id}`}>${Number(j.price).toFixed(2)}</div>}
                      <div
                        className="truncate flex items-center gap-0.5"
                        style={{ color: techNames ? color : undefined }}
                        data-testid={`month-job-tech-${j.id}`}
                      >
                        <User className="w-2.5 h-2.5 shrink-0" />
                        <span className={techNames ? "" : "text-muted-foreground italic"}>{techNames || "Unassigned"}</span>
                      </div>
                    </div>
                  );
                })}
                {dayJobs.length > 3 && (
                  <div className="text-[11px] text-muted-foreground pl-1">+{dayJobs.length - 3} more</div>
                )}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
